import { DialogTitle } from '@mui/material';
import { Loading, Table } from '@shape-ds/components';
import { useDatesHelper, buildColumnsObject } from '@shape/lighthouse-components';
import { eventsColumns } from 'pages/Asset/tabs/Events/hooks/useEventColumns';
import { useEventsQuery, useSimilarEventsQuery } from 'queries/events';
import { EventItem } from 'queries/events/types';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { groupEvents } from 'pages/Asset/tabs/Events/utils/EventGrouping';
import './style.scss';
import dayjs from 'dayjs';
import { DateRange } from 'queries/assets/types';
import { DateFilters, EmptyMessage } from '@shape/lighthouse-components';

export interface SimilarEventListProps {
  assetId: string;
  eventId?: string;
  isMonitoring?: boolean;
  showTitle?: boolean;
  customTitle?: React.ReactNode;
  className?: string;
  maxHeight?: number;
}
export const DEFAULT_PERIOD = 'all';

const SimilarEventList: React.FC<SimilarEventListProps> = ({
  assetId,
  eventId = '',
  isMonitoring,
  showTitle = true,
  customTitle,
  className,
  maxHeight = 190,
}) => {
  const { t } = useTranslation();
  const { buildDateRange } = useDatesHelper();

  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  const eventQuery = useEventsQuery(
    {
      filters: {
        start_date: dateRange?.startDate ?? '2000-01-01',
        end_date: dateRange?.endDate ?? undefined,

        assets: [{ id: assetId, name: '' }],
      },
      include_descendants: false,
    },
    {
      enabled: isMonitoring,
    },
  );
  const similarEventQuery = useSimilarEventsQuery(
    {
      assetId,
      eventId,
      end_date: dateRange?.endDate ?? undefined,
      start_date: dateRange?.startDate,
    },
    { enabled: !isMonitoring },
  );

  const { data, isLoading } = isMonitoring ? eventQuery : similarEventQuery;

  const columns = buildColumnsObject<EventItem>({
    columns: eventsColumns,
    overrideColumns: [
      'severity',
      'alarm_datetime_start',
      'duration',
      'description',
      'status',
      'classification',
      'agreg',
      'see_event',
    ],
    getLabel: (key) => t(`event_page:table.${key}`),
  });
  const periodOptions = ['90', '180', '365', 'all'].map((option) => ({
    label:
      option !== 'all'
        ? t('components:date_filter.last_n_days', { days: option })
        : t('components:date_filter.all', { days: option }),
    value: option,
  }));
  const onChangePeriod = (value: string) => {
    setDateRange(
      value === 'all'
        ? { startDate: '2000-01-01', endDate: dayjs().format('YYYY-MM-DD') }
        : buildDateRange(value),
    );
  };
  const groupedData = useMemo(() => {
    if (!data) return [];
    return groupEvents(
      data?.events.map((event) =>
        'event_id' in event
          ? {
              ...event,
              id: event.event_id,
              alarm_datetime_start: event.start_date,
              alarm_datetime_end: event.end_date,
              asset: {
                id: assetId,
              },
            }
          : event,
      ) as EventItem[],
    );
  }, [data]);
  const parentRef = useRef<HTMLDivElement>(null);

  return (
    <div className={`similar-events ${className}`} ref={parentRef}>
      <DialogTitle className="similar-events-list-title" data-testid="dialog-title">
        {showTitle && t('event_page:components.similarEventsList.title')}
        {customTitle}
        <DateFilters
          periodOptions={periodOptions}
          enableCalendar={false}
          defaultPeriod={DEFAULT_PERIOD}
          handlePeriodChange={onChangePeriod}
          data-testid="date-filters"
          className="similar-events-date-filters"
        />
      </DialogTitle>
      {isLoading && <Loading data-testid="loading" center />}
      {!isLoading && data?.events.length === 0 && (
        <EmptyMessage
          message={t(`event_page:components.similarEventsList.empty_message`)}
          data-testid="empty-message"
        />
      )}

      {data && data?.events.length > 0 && (
        <Table<EventItem>
          style={{ maxHeight: '15vh' }}
          className="similar-events-table"
          columns={columns}
          data={groupedData || []}
          data-testid="similar-event-table"
        />
      )}
    </div>
  );
};

export default SimilarEventList;
